import React from 'react';
import PropTypes from 'prop-types';

import BaseComponent from '../../baseClasses/BaseComponent'; 
import dataModel from '../../models/DataModel';
import FileUtil from '../../helpers/FileUtil/FileUtil';
import ModalBannerConstants from '../../constants/ModalBannerConstants';
import ImageTools from '../../helpers/ImageTools';
import ProgressIndicator from '../ProgressIndicator';
import heic2any from 'heic2any';
import UploadCloudIcon from '../Iconoclast/UploadCloudIcon/index';
import PlusButtonIcon from '../../assets/images/add-blue-button.svg';

import styles from '../../assets/styles/globals.scss';
import './MultiFileUploader.scss';

export default class MultiFileUploader extends BaseComponent {
  constructor(props) {
    MultiFileUploader.propTypes = {
      backgroundIcon: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
      /** if true, handleFileSelect() will be sent an image preview */
      shouldGenerateImagePreview: PropTypes.bool,
      hasCloseButton: PropTypes.bool,
      handleClose: PropTypes.func,
      /** if true, the component will be overlayed with a scrollable loader as uploads are in progress */
      hasInlineLoader: PropTypes.bool,
      /** file upload/select handler, ideally this is an async function */
      handleFileSelect: PropTypes.func,
      /** called after handleFileSelect completes */
      handleUploadsComplete: PropTypes.func,
      currentNumberOfFiles: PropTypes.number,
      maximumAllowedFiles: PropTypes.number,
      /** supported filetypes, each starting with a '.' e.g. ['.jpg']
       * If an empty array is passed, all types are accepted.
       */
      acceptedFileTypes: PropTypes.arrayOf(PropTypes.string),
      /** whether this is in a modal display (i.e. <FileUploadModal>) */
      modal: PropTypes.bool,
      /** hide input so it remains clickable programmatically */
      hideInput: PropTypes.bool,
    };

    super(props);
    this.state = {
      filesInProgress: new Map(),
      withinDragZone: false,

    };
    this.validImageTypes = new Set(FileUtil.getAcceptedExtensionsByType('jpg', true, true));
    this.acceptedTypesStr = props.acceptedFileTypes ? props.acceptedFileTypes.join() : '';
    this.onlyImagesAccepted = this.areOnlyImagesAccepted();

    this.clickFileInput = this.clickFileInput.bind(this);
  }

  areOnlyImagesAccepted() {
    const { acceptedFileTypes } = this.props;
    if (acceptedFileTypes && acceptedFileTypes.length) {
      for (let fileType of this.props.acceptedFileTypes) {
        fileType = fileType.substr(1); // remove '.'
        if (!this.validImageTypes.has(fileType)) {
          return false;
        }
      }
      return true;
    }
    else return false;
  }

  async convertHeicToJpeg(file) {
    const isHeic = file.name.toLocaleLowerCase().endsWith('.heic');
  
    if (isHeic) {
      const blob = await heic2any({
        blob: file,
        toType: 'image/jpg',
      });
  
      const fileNameWithoutExtension = file.name.replace('.heic', '');

      return new File(
        Array.isArray(blob) ? blob : [blob],
        `${fileNameWithoutExtension}.jpg`
      );
    }
  };

  async getImagePreviewFromFile(file){
    return new Promise ((resolve, reject) => {
      let reader = new FileReader();
      reader.onload = () => {
        resolve(reader.result);
      };
      reader.readAsDataURL(file);
    });
  }

  async handleMultiFileSelect(e) {
    const {
      currentNumberOfFiles,
      maximumAllowedFiles, 
      handleFileSelect,
      acceptedFileTypes,
      handleUploadsComplete, 
      shouldGenerateImagePreview,
      hasInlineLoader,
      handleClose,
      modal,
    } = this.props;

    this.setState({ withinDragZone: false });
    const { filesInProgress } = this.state;
    
    e.preventDefault();
    let files = e.target.files;
    if (!files.length){
      return;
    }
    else if (files.length > maximumAllowedFiles - currentNumberOfFiles) {
      const numberOfFilesLeft = maximumAllowedFiles - currentNumberOfFiles;
      let fileTypeStr = this.onlyImagesAccepted ? 'image' : 'file';
      const message = `Only ${maximumAllowedFiles} ${fileTypeStr}${maximumAllowedFiles == 1 ? ' is' : 's are'} allowed. 
          Please select no more than ${numberOfFilesLeft} ${fileTypeStr + (numberOfFilesLeft == 1 ? '' : 's')}.`;
      this.showErrorModal(message);
    }
    else {
      this.totalFiles = files.length;
      const uploadPromises = [];
      for (let index = 0; index < files.length; index++) {
        let file = files[index];
        if (acceptedFileTypes.length && !this.acceptedTypesStr.includes(FileUtil.getExtension(file.name))) {
          this.showErrorModal(`Only files with the extensions ${acceptedFileTypes.join(', ')} are allowed on this Completion Step.`);
          return;
        }
        else {
          if (hasInlineLoader) {
            // add file to loading list and save index
            filesInProgress.set(index, file);
            this.setState({ filesInProgress });
          }
          let fileExtension = FileUtil.getExtension(file.name);
          if(fileExtension.toLowerCase() === 'heic'){
            try{
              file = await this.convertHeicToJpeg(file);
              fileExtension = FileUtil.getExtension(file.name);
            }
            catch(error){
              this.showErrorModal('Error converting HEIC to JPG');
            }
          }
          const isImage = this.validImageTypes.has(fileExtension.toLowerCase());
          let fileSelectPromise;
          if (isImage && shouldGenerateImagePreview) {
            const preview = await this.getImagePreviewFromFile(file);
            fileSelectPromise = handleFileSelect(file, preview, this.totalFiles);
          }
          else{
            fileSelectPromise = handleFileSelect(file, null, this.totalFiles);
          }
          if (hasInlineLoader && filesInProgress.has(index)) {
            // remove file from loading list after complete
            if (fileSelectPromise && fileSelectPromise.then) {
              uploadPromises.push(fileSelectPromise);
              fileSelectPromise.then(() => {
                filesInProgress.delete(index);
                this.setState({ filesInProgress });
              }).catch(error => {
                filesInProgress.delete(index);
                this.setState({ filesInProgress });
                console.log(error);
              });
            }
            else {
              filesInProgress.delete(index);
            }
          }
        }
      }
      Promise.all(uploadPromises).then(results => {
        if (handleUploadsComplete) handleUploadsComplete();
      });
      if (modal && !hasInlineLoader) {
        handleClose();
      }
      
    }
  }

  handleDragEnter() {
    this.setState({ withinDragZone: true });
  }
  handleDragLeave() {
    this.setState({ withinDragZone: false });
  }

  clickFileInput(e) {
    e.stopPropagation();
    this.uploadInput.click();
  }

  showErrorModal(message) {
    const {
      currentNumberOfFiles,
      maximumAllowedFiles,
      handleFileSelect,
      acceptedFileTypes,
      modal,
    } = this.props;

    dataModel.showGlobalModal('SYSTEM_POPUP', {
      message: message,
      okButtonText: 'OK',
      oneButton: true,
      height: 184,
      width: 320,
      captionText: 'ERROR',
      handleOwnerOk: () => {
        if (modal) {
          // re-show the modal
          dataModel.showGlobalModal(ModalBannerConstants.FILE_UPLOADER, {
            acceptedFileTypes,
            handleFileSelect,
            currentNumberOfFiles,
            maximumAllowedFiles,
          });
        }
      },
    });
  }

  createLoadingList() {
    const fileList = [];
    let index = 1;
    this.state.filesInProgress.forEach((file) => {
      const isEvenRow = index % 2 == 0; 
      const extension = FileUtil.getExtension(file.name);
      const imgSrc = ImageTools.getIconForFile(extension);
      fileList.push(
        <span key={'file-'+index} className={'loading-list-item' + (isEvenRow ? ' loading-list-item--even' : '')}>
          <img className='loading-list-item__img' alt='file-type' src={imgSrc}/> 
          <p className='loading-list-item__name'>{file.name}</p>
          <ProgressIndicator width={20} height={2}/>
        </span>
      );
      index++;
    }); 
    return (
      <div
      className={'loading-list'}>
        {fileList}
      </div>
    );
  }

  render() {
    const { hasCloseButton, handleClose, backgroundIcon, hasInlineLoader } = this.props;
    const { withinDragZone, filesInProgress } = this.state;
    // hideInput prop
    const { hideInput } = this.props;
    const hidden = hideInput ? 'hidden' : '';

    const plusIcon = <img src={PlusButtonIcon} alt='upload-file-button'/>;

    let backgroundImage = (typeof backgroundIcon == 'string') 
      ? <img src={backgroundIcon} alt='uploader-background'/>
      : (backgroundIcon || <UploadCloudIcon color={styles.lightElement}/>);

    const fileInput = <input className="input + " type="file" accept={this.acceptedTypesStr} multiple onChange={ this.handleMultiFileSelect } ref={el => this.uploadInput = el} />;
    return (
      <div className={`multi-file-uploader ${hidden}`} >
        {
          hasCloseButton &&
          <span className='multi-file-uploader__x' onClick={handleClose}>&times;</span>
        }
        <div className={'multi-file-uploader__container'} onClick={this.clickFileInput} onDragEnter={this.handleDragEnter}>
          <div className="multi-file-uploader__img">{backgroundImage}</div>
          <div className="multi-file-uploader__text">drag and drop here or select {plusIcon} to browse</div>
          { (hasInlineLoader && filesInProgress.size) ? this.createLoadingList() : null }
        </div>
        {
          withinDragZone ?
            <div className='input-container' onClick={this.clickFileInput} onDragLeave={this.handleDragLeave}>
              {fileInput}
            </div>
            : fileInput
        }
      </div>
    );
  }
}

MultiFileUploader.defaultProps = {
  acceptedFileTypes: [],
}; 



// WEBPACK FOOTER //
// ./src/components/MultiFileUploader/index.js